import classNames from 'classnames'
import React, {useRef, useState} from 'react'

import useOutsideAlerter from '../../hooks/useOutsideAlerter'
import Flag from '../icons/Flag'
import Icon from '../icons/Icon'

export enum AvailableLanguageCode {
  en = 'GB',
  ee = 'EE',
  ru = 'RU',
  lt = 'LT',
  lv = 'LV',
}

export interface LanguageSelectOption {
  key: AvailableLanguageCode;
  label: string;
}

export interface LanguageSelectProps {
  options: LanguageSelectOption[];
  compact?: boolean;
  selectedLanguage: LanguageSelectOption;
  onLanguageChange: (newValue: LanguageSelectOption) => void;
}

const LanguageSelect = ({
  options,
  compact,
  selectedLanguage,
  onLanguageChange
}: LanguageSelectProps) => {
  const wrapperRef = useRef(null)
  const [isLanguageSelectOpen, setLanguageSelectOpen] = useState<boolean>(
    false
  )

  const onLanguageSelect = (newValue: LanguageSelectOption) => {
    setLanguageSelectOpen(false)
    onLanguageChange(newValue)
  }

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>,
    newValue: LanguageSelectOption
  ) => {
    if (e.key === 'Enter') {
      onLanguageSelect(newValue)
    }
  }

  useOutsideAlerter(wrapperRef, isLanguageSelectOpen, () =>
    setLanguageSelectOpen(false)
  )

  const wrapperClassNames = classNames('transition-all', 'text-black', {
    'w-full': !compact,
    'w-fit': compact,
    'lg:min-w-[220px] min-w-[185px]': compact && isLanguageSelectOpen,
    'min-w-[125px]': compact && !isLanguageSelectOpen
  })

  const compactButtonClassNames = classNames(
    'pl-[15px] pt-[15px] pb-[16px] flex items-center w-full rounded-t-xl border-t border-x',
    {
      'border-transparent': compact && !isLanguageSelectOpen,
      'bg-white border-[#E2E8F0] shadow-sm': compact && isLanguageSelectOpen,
      'pr-[15px]': isLanguageSelectOpen
    }
  )

  const largeButtonClassNames = classNames(
    'px-[15px] py-[9px] flex items-center bg-white w-full',
    {
      'bg-white rounded-xl border border-[#E2E8F0] shadow-sm': !isLanguageSelectOpen,
      'bg-white rounded-t-xl border-t border-x border-[#E2E8F0] shadow-sm': isLanguageSelectOpen
    }
  )

  return (
    <div ref={wrapperRef} className={wrapperClassNames}>
      <button
        type="button"
        onClick={() => setLanguageSelectOpen(!isLanguageSelectOpen)}
        className={compact ? compactButtonClassNames : largeButtonClassNames}
      >
        <div className="flex items-center flex-1">
          <Flag size="lg" countryCode={selectedLanguage.key} />
          <span className="ml-2 text-base font-normal lg:text-lg font-titillium">
            {selectedLanguage.label}
          </span>
        </div>
        <Icon icon={isLanguageSelectOpen ? 'chevron-up' : 'chevron-down'} />
      </button>
      {isLanguageSelectOpen &&
        <div className="bg-white last:rounded-b-lg border-x border-b border-[#E2E8F0] lg:py-[0px] py-[12px]">
          {options
            .filter(lang => lang.key !== selectedLanguage.key)
            .map(lang =>
              <div
                key={lang.key}
                tabIndex={0}
                onKeyDown={e => handleKeyDown(e, lang)}
                onClick={() => onLanguageSelect(lang)}
                className="flex items-center py-[5px] px-[15px] hover:bg-[#009fdd0d] cursor-pointer"
              >
                <Flag countryCode={lang.key} size="lg" />
                <span className="ml-2 text-base font-normal lg:text-lg font-titillium">
                  {lang.label}
                </span>
              </div>
            )}
        </div>
      }
    </div>
  )
}

export default LanguageSelect
